/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/
import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ContentContainer } from '@ziynx/components/layout';
import { Heading } from '@ziynx/components/typography';
import { withAppManager, setupCompanyVideos } from '@ziynx/components/utilities';
import ActionTypes from '@ziynx/action-types';
import Colors from '@ziynx/theme/colors';
import Card from '@ziynx/components/cards';
import Link from '@ziynx/components/links';
import UploadVideoModal from '@ziynx/pages/Modals/UploadVideo';
import type { MongoIdType, Viewports } from '@ziynx/flow-types/globals';
import type { VideoUploadConfig, RefinedCompanyVideos } from '@ziynx/flow-types/companyProfile';
import type { ManagedProps } from '@ziynx/flow-types/appManager';

type DispatchAction = {
  type: string,
  body?: [
    {
      op: 'replace' | 'remove',
      path: string,
      value?: any,
    }
  ]
} => void;

type History = {
  push: (pathName: string, {from: string}) => void,
  location: {
    state: {
      from: string
    }
  }
};

type Company = {
  _id?: MongoIdType,
  name: string,
  headline: string,
  industries: Array<{
    name: string,
  }>,
  success: boolean,
  patching: boolean,
  checking: boolean,
  videos: Array<RefinedCompanyVideos>,
};

type Props = $ReadOnly<{|
  /**
   * Viewport
   */
  viewport?: Viewports,
|}>;

const PageContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 688px;
  min-width: 288px;
  padding: ${({ isMobile }) => (isMobile ? '48px 16px' : '40px')};
  ${({ isMobile }) => isMobile && 'margin: auto;'}
`;

const PageHeadingWrapper = styled.div`
  padding-top: 48px;
  padding-bottom: 48px;
`;

const StyledCard = styled(Card)`
  ${({ lastChild }) => !lastChild && 'margin-bottom: 24px;'};
`;

const EditVideosScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;

  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';

  const [isModalOpen, setModalOpen] = useState(false);

  const [videoUploadConfig, setVideoConfig] = useState({ videoIndex: 0 });

  const dispatch: DispatchAction = useDispatch();

  const history: History = useHistory();

  const companyService: Company = useSelector(({ company }) => {
    const myCompany = company?.me || {};
    const videoArray = setupCompanyVideos(myCompany?.videos);
    return {
      ...myCompany,
      videos: videoArray,
    };
  }, shallowEqual);

  const _closeVideoModal = (): void => {
    setModalOpen(false);
    setVideoConfig({ videoIndex: 0 });
  };

  const _openVideoModal = (uploadConfig: VideoUploadConfig): void => {
    setVideoConfig(uploadConfig);
    setModalOpen(true);
  };

  const _removeVideo = (videoIndex: number): void => {
    dispatch({
      type: ActionTypes.company.patch.START,
      body: [
        {
          op: 'remove',
          path: `videos[${videoIndex}]`,
        },
      ],
    });
  };

  return (
    <>
      <Helmet>
        <title>{`${companyService?.name ?? ''} | Edit | ZIYNX`}</title>
      </Helmet>
      <ContentContainer>
        <UploadVideoModal
          open={isModalOpen}
          videoUploadConfig={videoUploadConfig}
          onClose={_closeVideoModal}
        />
        <PageContentWrapper isMobile={isMobile}>
          <Link
            variant="caret"
            onClick={() => {
              history.push('/company-profile', { from: 'edit-videos' });
            }}
            color={Colors.GrayDark}
          >
            Back to company profile
          </Link>
          <PageHeadingWrapper>
            <Heading large color={Colors.GrayVeryDark}>
              Edit videos
            </Heading>
          </PageHeadingWrapper>

          {companyService?.videos.map((item, index: number) => {
            let mediaUploadReferenceId = '';
            let imageAltText = '';

            switch (index) {
            case 0:
              mediaUploadReferenceId = 'companyVideoOne';
              imageAltText = 'Company Video One';
              break;
            case 1:
              mediaUploadReferenceId = 'companyVideoTwo';
              imageAltText = 'Company Video Two';
              break;
            case 2:
            default:
              mediaUploadReferenceId = 'companyVideoThree';
              imageAltText = 'Company Video Three';
              break;
            }

            const existingThumbnail = companyService?.videos?.[index]?.thumbnail_optimized?.uri
            || companyService?.videos?.[index]?.thumbnail?.uri;

            const thumbnail = item?.thumbnail_optimized?.uri || item?.thumbnail?.uri;

            return (
              <StyledCard
                key={item?._id ?? index.toString()}
                variant="videoPreview"
                title={item?.title}
                rightAction={{
                  popover: {
                    position: isMobile ? 'bottomLeft' : 'bottomRight',
                    links: [
                      {
                        children: 'Rename title',
                        onClick: () => {
                          _openVideoModal({
                            videoIndex: index,
                            referrer: 'Rename Title Popover',
                            existingVideo: {
                              title: companyService?.videos?.[index]?.title,
                              uri: companyService?.videos?.[index]?.video?.uri,
                              thumbnail: { uri: existingThumbnail },
                            },
                          });
                        },
                      },
                      {
                        children: 'Change video',
                        onClick: () => {
                          _openVideoModal({
                            mediaUploadReferenceId,
                            referrer: 'Change Video Popover',
                            videoIndex: index,
                            existingVideo: {
                              title: companyService?.videos?.[index]?.title,
                              uri: companyService?.videos?.[index]?.video?.uri,
                              thumbnail: { uri: existingThumbnail },
                            },
                            isChange: true,
                          });
                        },
                      },
                      {
                        children: 'Remove video',
                        onClick: () => _removeVideo(index),
                        color: Colors.SecondaryRed,
                      },
                    ],
                  },
                }}
                link={{
                  onClick: () => {
                    _openVideoModal({
                      referrer: 'Add Video CTA',
                      mediaUploadReferenceId,
                      videoIndex: index,
                    });
                  },
                }}
                image={{
                  imageSource: thumbnail,
                  imageAltText,
                }}
              />
            );
          })}

        </PageContentWrapper>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(EditVideosScreen): React$ComponentType<ManagedProps<Props>>);
